<div class="job-apply-main">
  <!-- Progress Steps -->
  <div class="apply-progress">
    <!-- First step (always shown, automatically active if logged in) -->
    <div class="apply-progress__step" [class.active]="currentStep >= 1 || authService.isLoggedIn()">
      <div class="apply-progress__number">1</div>
      <div class="apply-progress__label">{{ 'jobApply.steps.basicInfo' | transloco }}</div>
    </div>
    <div class="apply-progress__line"></div>

    <!-- Second step -->
    <div class="apply-progress__step" [class.active]="currentStep >= 2">
      <div class="apply-progress__number">2</div>
      <div class="apply-progress__label">{{ 'jobApply.steps.profileResume' | transloco }}</div>
    </div>
    <div class="apply-progress__line"></div>

    <!-- Third step -->
    <div class="apply-progress__step" [class.active]="currentStep === 3">
      <div class="apply-progress__number">3</div>
      <div class="apply-progress__label">{{ 'jobApply.steps.confirmation' | transloco }}</div>
    </div>
  </div>

  <!-- Step 1: Email Form (only shown if user is not logged in) -->
  <div class="apply-form-container" *ngIf="currentStep === 1 && !authService.isLoggedIn()">
    <app-card>
      <div class="apply-form-content">
        <h2 class="apply-form-content__title">{{ 'jobApply.email.title' | transloco }}</h2>
        <p class="apply-form-content__desc">{{ 'jobApply.email.description' | transloco }}</p>

        <form [formGroup]="emailForm" (ngSubmit)="nextStep()" class="form">
          <div class="form__group">
            <app-input
              formControlName="email"
              [label]="'jobApply.email.emailLabel' | transloco"
              [type]="'email'"
              autocomplete="email"
              [errorMessage]="
                emailForm.get('email')?.invalid && emailForm.get('email')?.touched ? ('jobApply.errors.emailRequired' | transloco) : ''
              "></app-input>
          </div>

          <div class="form__actions">
            <app-button variant="outline" color="secondary" size="lg" routerLink="/" (click)="onCancel()">
              {{ 'button.cancel' | transloco }}
            </app-button>
            <app-button [disabled]="emailForm.invalid" [block]="true" size="lg" (click)="nextStep()">
              {{ 'jobApply.buttons.continue' | transloco }}
            </app-button>
          </div>
        </form>
      </div>
    </app-card>
  </div>

  <!-- Step 2: Personal Information Form -->
  <ng-content *ngIf="currentStep === 2">
    <div class="apply-form-container">
      <div class="apply-step-header">
        <h3>{{ 'profile.profileResume.title' | transloco }}</h3>
        <p>{{ 'profile.profileResume.description' | transloco }}</p>
      </div>

      <app-profile-form
        #profileForm
        [userEmail]="authService.isLoggedIn() ? authService.currentUserValue?.email || '' : emailForm.get('email')?.value || ''"
        [initialProfileData]="restoredProfileData"></app-profile-form>

      <!-- Missing data warning after profile form -->
      <app-alert-card *ngIf="missingDataWarning" class="missing-data-warning" type="warning">
        <div class="alert-content">
          <h4>{{ 'jobApply.missingData.title' | transloco }}</h4>
          <p>{{ missingDataWarning }}</p>
        </div>
      </app-alert-card>
    </div>

    <div class="apply-form-container" *ngIf="isProfileComplete">
      <div class="apply-form-container__header">
        <h3>{{ 'jobApply.attachments.title' | transloco }}</h3>
        <p>{{ 'jobApply.attachments.subtitle' | transloco }}</p>
      </div>
      <!-- Attachments Section -->
      <app-card class="attachments-section">
        <div class="attachments-content w-100">
          <!-- File Dropzone -->
          <div
            class="file-dropzone"
            [class.highlight]="isDragOver"
            (click)="attachmentFileInput.click()"
            (dragover)="onDragOver($event)"
            (dragleave)="onDragLeave($event)"
            (drop)="onDrop($event)">
            <div class="file-dropzone__upload-area">
              <div class="file-dropzone__icon">
                <i class="fas fa-cloud-upload-alt"></i>
              </div>
              <div class="file-dropzone__text">
                <p>{{ 'jobApply.attachments.dragDrop' | transloco }}</p>
                <span>{{ 'jobApply.attachments.or' | transloco }}</span>
              </div>
              <app-button type="button" size="md" variant="outline" color="secondary" (click)="onBrowseClick($event)">
                {{ 'jobApply.attachments.browse' | transloco }}
              </app-button>
              <p class="file-dropzone__info">{{ 'jobApply.attachments.supportedFormats' | transloco }}</p>
              <input type="file" hidden #attachmentFileInput (change)="onAttachmentFileChange($event)" accept=".pdf,.doc,.docx" multiple />
            </div>
          </div>

          <!-- Uploaded Files List -->
          <div class="attachments-list mt-5" *ngIf="attachments.length > 0">
            <div class="attachment-item" [class.attachment-item--pending]="!attachment.type" *ngFor="let attachment of attachments; let i = index">
              <div class="attachment-item__icon">
                <i class="fas" [ngClass]="getFileIcon(attachment.file.type)"></i>
              </div>
              <div class="attachment-item__details">
                <div class="attachment-item__name">{{ attachment.file.name }}</div>
                <div class="attachment-item__meta">
                  <span class="attachment-item__size">{{ formatFileSize(attachment.file.size) }}</span>
                  <span class="attachment-item__type" *ngIf="attachment.type">• {{ 'jobApply.attachments.types.' + attachment.type | transloco }}</span>
                  <span class="attachment-item__status" *ngIf="attachment.uploading">
                    <i class="fas fa-spinner fa-spin"></i>
                    {{ 'jobApply.attachments.uploading' | transloco }}
                  </span>
                  <span class="attachment-item__status attachment-item__status--success" *ngIf="attachment.uploaded && !attachment.uploading">
                    <i class="fas fa-check"></i>
                    {{ 'jobApply.attachments.uploaded' | transloco }}
                  </span>
                  <span class="attachment-item__status attachment-item__status--error" *ngIf="attachment.error">
                    <i class="fas fa-exclamation-circle"></i>
                    {{ attachment.error }}
                  </span>
                </div>
                <!-- Inline Type Selector (shown when type not yet selected) -->
                <div class="attachment-item__type-selector" *ngIf="!attachment.type">
                  <span class="attachment-item__type-label">{{ 'jobApply.attachments.selectType' | transloco }}</span>
                  <div class="attachment-item__type-options">
                    <app-button type="button" variant="outline" color="secondary" size="sm" (click)="setAttachmentType(i, 'cv')">
                      {{ 'jobApply.attachments.types.cv' | transloco }}
                    </app-button>
                    <app-button type="button" variant="outline" color="secondary" size="sm" (click)="setAttachmentType(i, 'coverLetter')">
                      {{ 'jobApply.attachments.types.coverLetter' | transloco }}
                    </app-button>
                    <app-button type="button" variant="outline" color="secondary" size="sm" (click)="setAttachmentType(i, 'certificate')">
                      {{ 'jobApply.attachments.types.certificate' | transloco }}
                    </app-button>
                    <app-button type="button" variant="outline" color="secondary" size="sm" (click)="setAttachmentType(i, 'portfolio')">
                      {{ 'jobApply.attachments.types.portfolio' | transloco }}
                    </app-button>
                    <app-button type="button" variant="outline" color="secondary" size="sm" (click)="setAttachmentType(i, 'other')">
                      {{ 'jobApply.attachments.types.other' | transloco }}
                    </app-button>
                  </div>
                </div>
              </div>
              <div class="attachment-item__actions">
                <app-button type="button" variant="invisible" color="secondary" size="sm" (click)="removeAttachment(i)" [disabled]="attachment.uploading">
                  <i class="fas fa-trash"></i>
                </app-button>
              </div>
            </div>
          </div>
        </div>
      </app-card>
    </div>

    <!-- GDPR Consent and Submit Section -->
    <div class="apply-form-container">
      <!-- GDPR Consent -->
      <div class="gdpr-consent-section" *ngIf="isProfileComplete">
        <form [formGroup]="resumeAndCoverForm">
          <app-input-checkbox
            formControlName="gdprConsent"
            [errorMessage]="
              resumeAndCoverForm.get('gdprConsent')?.invalid && resumeAndCoverForm.get('gdprConsent')?.touched ? ('jobApply.gdprConsent.error' | transloco) : ''
            ">
            <span [innerHTML]="gdprConsentHtml"></span>
          </app-input-checkbox>
        </form>
      </div>

      <!-- Missing data warning before submit -->
      <app-alert-card *ngIf="missingDataWarning" class="missing-data-warning" type="warning">
        <div class="alert-content">
          <h4>{{ 'jobApply.missingData.title' | transloco }}</h4>
          <p>{{ missingDataWarning }}</p>
        </div>
      </app-alert-card>

      <!-- Error message display -->
      <app-alert-card *ngIf="submissionError" class="submission-error" type="error">
        <div class="alert-content">
          <h4>{{ 'jobApply.errors.title' | transloco }}</h4>
          <p>{{ submissionError }}</p>
        </div>
      </app-alert-card>

      <div class="form__actions">
        <app-button size="lg" type="button" variant="outline" (click)="prevStep()" [disabled]="isSubmitting">
          {{ 'jobApply.buttons.back' | transloco }}
        </app-button>
        <app-button size="lg" type="button" [disabled]="isSubmitDisabled || isSubmitting" (click)="submitApplication()">
          <span *ngIf="!isSubmitting">{{ 'jobApply.buttons.submit' | transloco }}</span>
          <span *ngIf="isSubmitting">{{ 'jobApply.buttons.submitting' | transloco }}</span>
        </app-button>
      </div>
    </div>
  </ng-content>

  <!-- Step 3: Confirmation -->
  <div class="apply-form-container" *ngIf="currentStep === 3">
    <div class="card">
      <div class="card__content">
        <div class="apply-success">
          <div class="apply-success__icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h2 class="apply-success__title">{{ 'jobApply.confirmation.title' | transloco }}</h2>
          <p class="apply-success__message">{{ 'jobApply.confirmation.message' | transloco }}</p>
          <div class="apply-success__actions">
            <button type="button" class="btn btn--primary" routerLink="/">
              {{ 'jobApply.buttons.viewAllJobs' | transloco }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
